import React, { Fragment, useEffect, useState } from "react";

import LineChartPlotly from "components/charts/LineChartPlotly.component";
import { Grid, useMediaQuery, useTheme } from "@mui/material";
import BarChartPlotly from "components/charts/BarChartPlotly.component";
import HorizontalBarChartPlotly from "components/charts/HorizontalBarChart.component";
import {
  getMinMaxLineChart,
} from "constants/charts";
import MinMaxMeanCards from "components/cards/avg/MinMaxMeanCards.component";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { red } from "@mui/material/colors";
import { getTimeZoneStandard, getTimeZoneStandardInteger } from "utils/timezone";

const MetricContainer = ({
  metric,
  data,
  viewHorChart,
  viewLineChart = true,
  viewBarChart = true,
  symbol,
  yLabel,
  xLabel,
  colorTo,
  colorFrom,
  colorAvg,
  shapes,
  children,
  info,
  xRange,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const matches = useMediaQuery("(max-width:1300px)");

  const [dataLineChart, setDataLineChart] = useState(null);
  const [dataMin, setDataMin] = useState(null);
  const [dataMax, setDataMax] = useState(null);
  const [dataMean, setDataMean] = useState(null);
  const [meanHours, setMeanHours] = useState(null);
  const [zoneMean, setZoneMean] = useState(null);
  const [time, setTime] = useState(null);
  const [range, setRange] = useState({ min: null, max: null });

  useEffect(() => {
    if (data && !_.isEmpty(data) && data?.time && !_.isEmpty(data?.time)) {
      const newTime = [];
      data?.time.map((item) => {
        return newTime.push(getTimeZoneStandard(item));
      });
      setTime(newTime);
    }
  }, [data, i18n]);

  useEffect(() => {
    if (data?.avg?.min || data?.avg?.min === 0) {
      if (data?.avg?.min === 0) {
        setDataMin(0);
      } else {
        setDataMin(data?.avg?.min);
      }
    }
    if (data?.avg?.max || data?.avg?.max === 0) {
      if (data?.avg?.max === 0) {
        setDataMax(0);
      } else {
        setDataMax(data?.avg?.max);
      }
    }
    if (data?.avg?.mean || data?.avg?.mean === 0) {
      if (data?.avg?.mean === 0) {
        setDataMean(0);
      } else {
        setDataMean(data?.avg?.mean);
      }
    }

    if (data?.hourly) {
      const newTime = [];
      data?.hourly?.hour?.map((item) => {
        return newTime.push(getTimeZoneStandardInteger(item));
      });

      const trace = {
        x: newTime,
        y: data?.hourly?.data,
        name: "Media oraria",
        marker: { color: colorAvg },
        type: "bar",
      };
      setMeanHours([trace]);
    }

    if (data?.by_cluster) {
      const trace = {
        x: data?.by_cluster?.data,
        y: data?.by_cluster?.cluster,
        name: "Zone",
        orientation: "h",
        marker: { color: colorAvg, width: 0.5 },
        type: "bar",
      };
      setZoneMean([trace]);
    }
  }, [data, colorAvg, time]);

  useEffect(() => {
    let traceChart = [];

    if (data?.avg?.data_min && time) {
      const dataLine = {
        x:
          !_.isEmpty(time) && !_.isEmpty(data?.avg?.data_min)
            ? time.filter((item, index) => data?.avg?.data_min[index] !== null)
            : [],
        y:
          !_.isEmpty(data?.avg?.data_min) &&
            data?.avg?.data_min.filter((item) => item !== null).length !== 0
            ? data?.avg?.data_min.filter((item) => item !== null)
            : [],
        textfont: { color: theme.palette.mode === "dark" ? "white" : "black" },
        fillcolor: `${colorFrom}80`,
        line: { color: colorFrom, width: 0 },
        type: "scatter",
        opacity: 0.4,
        name: "Min",
      };
      traceChart.push(dataLine);
    }
    if (data?.avg?.data && time) {
      const dataLine = {
        x:
          !_.isEmpty(time) && !_.isEmpty(data?.avg?.data)
            ? time.filter((item, index) => data?.avg?.data[index] !== null)
            : [],
        y:
          !_.isEmpty(data?.avg?.data) &&
            data?.avg?.data?.filter((item) => item !== null).length !== 0
            ? data?.avg?.data?.filter((item) => item !== null)
            : [],
        textfont: { color: theme.palette.mode === "dark" ? "white" : "black" },
        fillcolor: `${colorFrom}80`,
        line: { color: colorTo },
        name: t("Average"),
        fill: "tonexty",
        type: "scatter",
      };
      traceChart.push(dataLine);
    }
    if (data?.avg?.data_max && time) {
      const dataLine = {
        x:
          !_.isEmpty(time) && !_.isEmpty(data?.avg?.data_max)
            ? time.filter((item, index) => data?.avg?.data_max[index] !== null)
            : [],
        y:
          !_.isEmpty(data?.avg?.data_max) &&
            data?.avg?.data_max.filter((item) => item !== null).length !== 0
            ? data?.avg?.data_max.filter((item) => item !== null)
            : [],
        textfont: { color: theme.palette.mode === "dark" ? "white" : "black" },
        fillcolor: `${colorFrom}80`,

        line: { color: colorFrom, width: 0 },
        name: "Max",
        fill: "tonexty",
        type: "scatter",
      };
      traceChart.push(dataLine);
    }

    if (time && metric.value === "CO2") {
      const theshold = {
        x: !_.isEmpty(time) && [time[time.length - 1]],
        y: [700],
        mode: "lines+text",
        text: [`${t("Threshold of attention")}     `],
        textposition: "top",
        type: "scatter",
        line: { color: red[900], width: 1 },
        textfont: {
          size: 11,
          color: red[900],
        },
      };
      traceChart.push(theshold);
    }
    setDataLineChart(traceChart);
  }, [data, colorTo, colorFrom, colorAvg, time, theme, t, metric]);

  useEffect(() => {
    if (data?.avg?.data_min && data?.avg?.data_max) {
      setRange({
        min: _.min(data?.avg?.data_min),
        max: _.max(data?.avg?.data_max),
      });
    } else {
      if (dataMax && dataMin)
        setRange({
          min: dataMin,
          max: dataMax,
        });
    }
  }, [data, dataMax, dataMin]);

  return (
    <Fragment>
      {data && time && (
        <Grid container spacing={3}>

          <Grid item container spacing={3}>
            <Grid item xs={12} sm={2} md={matches ? 1.5 : 1.15} lg={1.15}>
              <MinMaxMeanCards
                colorFrom={colorFrom}
                colorTo={colorTo}
                min={dataMin}
                mean={dataMean}
                max={dataMax}
                symbol={symbol}
                info={info?.trace1}
              />
            </Grid>
            {viewLineChart &&
              !_.isEmpty(data?.avg?.data) &&
              !_.isEmpty(data?.avg?.data?.filter((item) => item !== null)) && (
                <Grid
                  item
                  xs={12}
                  sm={10}
                  md={matches ? 10.5 : 10.85}
                  lg={10.85}
                >
                  <LineChartPlotly
                    shapes={shapes}
                    data={dataLineChart}
                    yLabel={t(yLabel)}
                    yRange={
                      metric?.value === "bar_press"
                        ? [
                          range.min - Math.abs(range.min * 0.005),
                          range.max + Math.abs(range.max * 0.005),
                        ]
                        : (metric?.value === 'air_temp' || (metric?.value !== 'air_temp' && range.max !== 0)) ?
                          getMinMaxLineChart(range.min, range.max) : [0, 2]
                    }
                    showlegend={false}
                    xRange={xRange}
                  />
                </Grid>
              )}
          </Grid>
          <Grid item container spacing={3} xs={12} display='flex' alignItems='center'>
            {viewHorChart &&
              !_.isEmpty(data?.by_cluster?.data) &&
              !_.isEmpty(
                data?.by_cluster?.data?.filter((item) => item !== null)
              ) && (
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <HorizontalBarChartPlotly
                    data={zoneMean}
                    xLabel={t(yLabel)}
                    xRange={
                      yLabel === "Atmospheric_pressure_symbol"
                        ? [
                          range.min - Math.abs(range.min * 0.005),
                          range.max + Math.abs(range.max * 0.005),
                        ]
                        : getMinMaxLineChart(range.min, range.max)
                    }
                    height={420}
                  />
                </Grid>
              )}

            {viewBarChart &&
              !_.isEmpty(data?.hourly?.data) &&
              !_.isEmpty(
                data?.hourly?.data?.filter((item) => item !== null)
              ) && (
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <BarChartPlotly
                    data={meanHours}
                    yLabel={t(yLabel)}
                    xLabel={t(xLabel)}
                    yRange={
                      yLabel === "Atmospheric_pressure_symbol"
                        ? [
                          range.min - Math.abs(range.min * 0.005),
                          range.max + Math.abs(range.max * 0.005),
                        ]
                        : getMinMaxLineChart(range.min, range.max)
                    }
                    height={350}
                  />
                </Grid>
              )}
            {children && (
              <Grid item xs={12}
                sm={12}
                md={((!viewBarChart && !viewHorChart) || (viewBarChart && viewHorChart)) ? 12 : 6}
                lg={((!viewBarChart && !viewHorChart) || (viewBarChart && viewHorChart)) ? 12 : 6}>
                {children}
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
    </Fragment>
  );
};

export default MetricContainer;
